import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Auto-save engine for drawer fields (redesign spec §1: no explicit Save
 * bars). Property fields save on blur or selection change; long text uses
 * the debounced variant so a mid-thought blur never fires a partial save
 * surprise. Errors surface quietly next to the field instead of blocking.
 */

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface AutoSave<T> {
  state: SaveState;
  error: string;
  /** Call with the latest value on blur or on select-change. */
  save: (value: T) => void;
  /**
   * Write anything still waiting on the debounce, now.
   *
   * The debounced variant clears its timer when the component unmounts, so a
   * save queued in the last 900ms of a drawer's life was simply dropped —
   * type into Notes, close the drawer straight after, and the words were gone
   * with nothing said. Found while fixing the Nurture note, which had a louder
   * version of the same fault.
   *
   * Call it on blur. On the immediate variant nothing is ever pending, so it
   * does nothing, and callers never have to know which one they are holding.
   */
  flush: () => void;
}

export function useAutoSave<T>(persist: (value: T) => Promise<unknown>): AutoSave<T> {
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState('');
  const lastSaved = useRef<T | undefined>(undefined);
  const settleTimer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(settleTimer.current);
    },
    [],
  );

  const save = useCallback(
    (value: T) => {
      if (lastSaved.current !== undefined && Object.is(lastSaved.current, value)) {
        return;
      }
      setState('saving');
      setError('');
      persist(value)
        .then(() => {
          lastSaved.current = value;
          setState('saved');
          window.clearTimeout(settleTimer.current);
          settleTimer.current = window.setTimeout(() => setState('idle'), 1600);
        })
        .catch((cause: unknown) => {
          setState('error');
          setError(cause instanceof Error ? cause.message : 'Could not save');
        });
    },
    [persist],
  );

  // Nothing is ever queued here — save() writes immediately — so there is
  // nothing to flush. Present so both variants share one shape.
  const flush = useCallback(() => undefined, []);

  return { state, error, save, flush };
}

/** Debounced variant for free text: saves quietly while the person types. */
export function useDebouncedAutoSave<T>(
  persist: (value: T) => Promise<unknown>,
  delayMs = 900,
): AutoSave<T> {
  const base = useAutoSave(persist);
  const timer = useRef<number | undefined>(undefined);
  // The value the timer is holding. Kept so flush() can write it; without it
  // the pending edit exists only inside a closure the unmount cleanup throws
  // away, which is exactly how the words were being lost.
  const pending = useRef<{ value: T } | null>(null);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
    },
    [],
  );

  const save = useCallback(
    (value: T) => {
      window.clearTimeout(timer.current);
      pending.current = { value };
      timer.current = window.setTimeout(() => {
        pending.current = null;
        base.save(value);
      }, delayMs);
    },
    [base, delayMs],
  );

  const flush = useCallback(() => {
    if (pending.current === null) {
      return;
    }
    window.clearTimeout(timer.current);
    const { value } = pending.current;
    pending.current = null;
    base.save(value);
  }, [base]);

  return { state: base.state, error: base.error, save, flush };
}

export function SaveHint({ state, error }: { state: SaveState; error: string }) {
  if (state === 'idle') {
    return null;
  }
  if (state === 'error') {
    return <span className="ui-savehint ui-savehint--error">{error || 'Could not save'}</span>;
  }
  return <span className="ui-savehint">{state === 'saving' ? 'Saving…' : 'Saved'}</span>;
}
